import { useEffect } from "react";
import { useRouter } from "next/router";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { isOrtexApp } from "@/lib/utils";

const APP_HOSTS = ["app.ortex.com"];

/** The in-app path of a link the OS opened the app with, or "" when it is not one of ours. */
export const appPathOf = (url: string): string => {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return "";
    }
    if ((parsed.protocol === "https:" || parsed.protocol === "http:") && APP_HOSTS.includes(parsed.hostname)) {
        // "//host/x" would be a protocol-relative URL to another site: keep it an in-app path.
        return (parsed.pathname || "/").replace(/^\/{2,}/, "/") + parsed.search + parsed.hash;
    }
    return "";
};

/**
 * Universal Links (iOS) and App Links (Android): when a https://app.ortex.com link is
 * tapped outside the app, the OS opens the app with it, and this routes to that page.
 * The OS side is the associated-domains entitlement / the verified intent filter, plus
 * public/.well-known/apple-app-site-association and assetlinks.json.
 */
const AppUrlListener = () => {
    const router = useRouter();

    useEffect(() => {
        if (!isOrtexApp()) return;
        const handle = App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
            const path = appPathOf(event.url);
            if (path) router.push(path);
        });
        return () => {
            handle.then((h) => h.remove()).catch(() => undefined);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};

export default AppUrlListener;
